import styles from "./styles.module.css";
import { updateRow, deleteRow } from "../../../utils/databaseactions";
import { useParams } from "react-router-dom";
import { useEffect, useState, useContext } from "react";
import { DatabaseValue } from "../../../contexts/database";
import { TypesValue } from "../../../contexts/types";
import { UserValue } from "../../../contexts/user";
import { MdClose } from "react-icons/md";
import Modal from "../../../modal";
import compare from "../../../utils/compare";
import formatForInput from "./utils/formatinput";
import formatForSubmit from "./utils/formatsubmit";
import { storage } from "../../../firebase";

export default function DataEditor({ updateData, setOpen, open, tableId, fields, initialData = {} }) {
  const [loadingTitle, setLoadingTitle] = useState("");
  const [fieldIds, setFieldIds] = useState(null);
  const [fnIsRunning, setFnIsRunning] = useState(false);
  const [data, setData] = useState(null);
  const [id, setId] = useState(null);
  const [files, setFiles] = useState({});
  const context = useContext(DatabaseValue);
  const types = useContext(TypesValue);
  const user = useContext(UserValue);
  const { appId } = useParams();

  const handleDelete = () => {
    if (window.confirm("Are you sure? This can't be undone...")) {
      setLoadingTitle(`Deleting row ${id}`);
      setFnIsRunning(true);
      const d = deleteRow(appId, tableId, id, user);
      d.then(() => {
        updateData();
      })
      .catch(err => {
        console.log(err);
      })
      .finally(() => setOpen(false));
    }
    else setOpen(false);
  }

  const uploadFiles = async (_files) => {
    const fileMap = {};
    for (let key in _files) {
      const _f = _files[key];
      const storageRef = storage.ref(`${appId}/files/${_f.name}`);
      const data = await storageRef
        .put(_f)
        .then(async snap => {
          const ref = snap.ref;
          const obj = {
            name: ref.name,
            fullPath: ref.fullPath,
            src: `https://uploads.brevitybuilder.com/${appId}/files/${ref.name}`,
            metadata: snap.metadata
          }
          return obj;
        });
      fileMap[key] = data;
    }
    return fileMap;
  }

  const update = async () => {
    let newData = Object.assign({}, data);
    if (compare(newData, initialData, false)) {
      console.log("no updates to make");
      return;
    }
    let fileMap = null;
    if (JSON.stringify(files) !== "{}") {
      fileMap = await uploadFiles(files);
    }
    Object.keys(newData).forEach(key => {
      const f = fields[key];
      if (f.type === "Image" || f.type === "File") {
        let filename = newData[key].name;
        newData[key] = fileMap[filename];
      }
      newData[key] = formatForSubmit(f.type, newData[key]);
      if (!!f.list) newData[key] = newData[key].length === 0 ? [] : newData[key].split(",");
    });
    setLoadingTitle(`Updating row ${id}`);
    setFnIsRunning(true);
    updateRow(appId, tableId, id, newData, user)
    .then(() => {
      updateData();
    })
    .catch(err => {
      console.log(err);
    })
    .finally(() => setOpen(false));
  }

  const handleChange = (val, field) => {
    let newData = Object.assign({}, data);
    newData[field] = val;
    setData(newData);
  }

  const handleJSONChange = (val, field, key) => {
    let newData = Object.assign({}, data);
    let obj = typeof newData[field] === "object" ? { ...newData[field] } : {};
    obj[key] = val;
    newData[field] = { ...obj} ;
    setData(newData);
  }

  const handleFileUpload = (target, field) => {
    let newData = Object.assign({}, data);
    const file = target.files[0];
    const fileInfo = {
      name: file.name,
      type: file.type,
      ext:  file.name.split(".").pop(),
      lastModified: file.lastModified,
      size: file.size
    };
    newData[field] = fileInfo;
    setFiles({...files, [fileInfo.name]: file});
    setData(newData);
  }

  const handleFileRemove = (fileName, field) => {
    const newFiles = Object.assign({}, files);
    delete newFiles[fileName];
    const newData = Object.assign({}, data);
    newData[field] = null;
    setFiles(newFiles);
    setData(newData);
  }
  
  useEffect(() => {
    if (!!initialData) {
      setId(initialData._id || null);
    }
  }, [initialData]);

  useEffect(() => {
    if (!!fields) {
      setFieldIds(Object.keys(fields));
    }
  }, [fields, open]);

  useEffect(() => {
    if (!!fieldIds && !!id) {
      if (!data) {
        let newData = Object.assign({}, initialData);
        fieldIds.forEach(key => {
          if (!fields[key].editable) delete newData[key];
          if (fields[key].type === "True/False" && !newData[key]) newData[key] = false;
          if (fields[key].type === "Location" && typeof newData[key] === "string") {
            let value = newData[key].replace(/"/g, "").replace("}", "");
            let [lat, lng] = value.split(",");
            const loc = { lat: lat.split(":")[1], lng: lng.split(":")[1]};
            newData[key] = loc;
          }
        });
        setData(newData);
      }
    }
  }, [fieldIds, initialData, data, fields, id]);

  useEffect(() => {
    if (!open) {
      setData(null);
      setFieldIds(null);
      setFnIsRunning(false);
      setLoadingTitle("");
      setFiles({});
    }
  }, [open, tableId]);

  const mapType = (type) => {
    for (let i = 0; i < types.length; i++) {
      if (types[i].name === type) return types[i];
    }
    return null;
  }

  const createInput = (f, fid) => {
    const type = mapType(f.type);
    const value = formatForInput(type.name, data[fid]);
    if (!type || type.name === "Text") {
      return <input 
              name={f.name}
              type="text" 
              value={value}
              onChange={(e) => handleChange(e.target.value, fid)}
              className="input" />;
    }
    if (type.name === "Location") {
      return (
        <div className={styles.split}>
          <div>
            <label>Latitude</label>
            <input 
              name={f.name + "-lat"}
              type="text"
              value={value && value.lat}
              onChange={(e) => handleJSONChange(e.target.value, fid, "lat")}
              className="input"
            />
          </div>
          <div>
            <label>Longitude</label>
            <input 
              name={f.name + "-lng"}
              type="text"
              value={value && value.lng}
              onChange={(e) => handleJSONChange(e.target.value, fid, "lng")}
              className="input"
            />
          </div>
        </div>
      );
    }
    if (type.name === "Number") {
      return <input 
              name={f.name}
              type="number" 
              value={value}
              onChange={(e) => handleChange(e.target.value, fid)}
              className="input" />;
    }
    if (type.name === "Date") {
      return <input 
              name={f.name}
              type="date" 
              value={value}
              onChange={(e) => handleChange(e.target.value, fid)}
              className="input" />;
    }
    if (type.name === "Image" || type.name === "File") {
      if (value && value.name) {
        return (
          <>
            <input
              type="text"
              value={value.name}
              className="input"
              disabled
            />
            <input
              type="button"
              onClick={() => handleFileRemove(value.name, fid)}
              value="Remove file"
              className="input"
            />
          </>
        )
      }
      else {
        return (
          <>
          <input
              type="text"
              value=""
              className="input"
              disabled
            />
            <input 
              name={f.name}
              type="file"
              accept="image/*"
              onChange={(e) => handleFileUpload(e.target, fid)}
              className="input"
            />
          </>
        )
      }
    }
    if (type.sqlType === "boolean") {
      return <input 
              name={f.name}
              type="checkbox" 
              checked={data[fid]}
              onChange={(e) => handleChange(e.target.checked, fid)}
              className="input" />;
    }
    if (type.sqlType === "jsonb") {
      return <textarea 
              name={f.name}
              value={value}
              onChange={(e) => handleChange(e.target.value, fid)}
              className="input" />
    }
    if (type.category === "database") {
      let tableName = "";
      for (let i = 0; i < context.length; i++) {
        if (context[i].id === type.name) {
          tableName = context[i].name;
          break;
        }
      }
      return <input 
              name={f.name}
              type="text" 
              value={value}
              placeholder={`Enter a ${tableName} id`}
              onChange={(e) => handleChange(e.target.value, fid)}
              className="input" />;
    }
  }

  if (!open) return null;
  return (
    <Modal onClose={() => setOpen(false)} open={open}>
      <div className={styles.form}>
        <div className={styles.formTitle}>
          Edit Data
          <MdClose onClick={() => setOpen(false)} className="icon" />
        </div>
        {fnIsRunning ? (
          <div className={styles.formContainer}>
            {loadingTitle}
          </div>
        ) : (
        <div className={styles.formContainer}>
          {(fieldIds || []).map(id => {
            const n = fields[id].name;
            if (!fields[id].editable) return null;
            return (
              <div key={id}>
                <div className={styles.formInputTitle}>{fields[id].name}</div>
                {!!data ? createInput(fields[id], id) : null}
              </div>
            )
          })}
        </div>
        )}
        <div className={styles.formContainer}>
          {fnIsRunning ? null : (
            <>
              <button disabled={false} onClick={update}>
                Submit
              </button>
              <button onClick={handleDelete}>
                Delete
              </button>
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}